/**
 * Service pour gérer l'historique des commandes fournisseurs.
 * Source de vérité : API backend (PostgreSQL), avec fallback cache localStorage.
 */

import { CommandeGroupee } from './CommandeFournisseurService';
import {
  commandeFournisseurApiService,
  CommandeFournisseurRecord,
} from './CommandeFournisseurApiService';

export type CommandeStatut = 'BROUILLON' | 'ENVOYEE' | 'RECUE' | 'ANNULEE' | 'GENEREE';

export interface CommandeHistorique {
  id: string;
  numeroCommande: string;
  dateCreation: string;
  devisInclus: string[];
  totaux: {
    montantMatieres: number;
    montantFournitures: number;
    montantTotal: number;
    nombreDevis: number;
  };
  statut: CommandeStatut;
  dateEnvoi?: string;
  dateReception?: string;
  notes?: string;
}

export interface DevisCommandeInfo {
  devisId: string;
  commandesAssociees: Array<{
    numeroCommande: string;
    dateCommande: string;
    statut: string;
  }>;
  derniereDateCommande?: string;
}

class CommandeHistoriqueService {
  private readonly STORAGE_KEY_HISTORIQUE = 'commandes_historique';
  private readonly STORAGE_KEY_DEVIS_COMMANDES = 'devis_commandes_info';
  private cache: CommandeHistorique[] = [];

  private genererIdHistorique(): string {
    return `hist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private lireLocalHistorique(): CommandeHistorique[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY_HISTORIQUE);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique local:", error);
      return [];
    }
  }

  private sauvegarderHistorique(historique: CommandeHistorique[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_HISTORIQUE, JSON.stringify(historique));
    } catch (error) {
      console.error("Erreur lors de la sauvegarde de l'historique local:", error);
    }
  }

  private getDevisCommandesInfoLocal(): Record<string, DevisCommandeInfo> {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY_DEVIS_COMMANDES);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Erreur lors de la récupération des infos devis:', error);
      return {};
    }
  }

  private sauvegarderDevisCommandesInfo(infos: Record<string, DevisCommandeInfo>): void {
    try {
      localStorage.setItem(this.STORAGE_KEY_DEVIS_COMMANDES, JSON.stringify(infos));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des infos devis:', error);
    }
  }

  private normalizeStatut(statut?: string): CommandeHistorique['statut'] {
    if (statut === 'GENEREE') return 'BROUILLON';
    if (statut === 'ENVOYEE') return 'ENVOYEE';
    if (statut === 'RECUE') return 'RECUE';
    if (statut === 'ANNULEE') return 'ANNULEE';
    return 'BROUILLON';
  }

  private mapApiRecord(record: CommandeFournisseurRecord): CommandeHistorique {
    const payload = record.payload || {};
    const devisInclus = record.devisInclus || payload.devisInclus || [];
    const totauxPayload = payload.totaux || record.totaux;
    const totaux = totauxPayload || {
      montantMatieres: 0,
      montantFournitures: 0,
      montantTotal: record.total_ht ?? 0,
      nombreDevis: devisInclus.length || 0,
    };

    return {
      id: record.id || this.genererIdHistorique(),
      numeroCommande: record.numeroCommande || payload.numeroCommande || '',
      dateCreation:
        record.dateCreation ||
        payload.dateCreation ||
        record.created_at ||
        new Date().toISOString(),
      devisInclus,
      totaux: totaux as CommandeHistorique['totaux'],
      statut: this.normalizeStatut(record.statut),
      dateEnvoi: payload.dateEnvoi,
      dateReception: payload.dateReception,
      notes: payload.notes,
    };
  }

  private mettreAJourCache(historique: CommandeHistorique[]): void {
    this.cache = historique;
    this.sauvegarderHistorique(historique);
  }

  async getHistorique(forceRefresh = false): Promise<CommandeHistorique[]> {
    try {
      const records = await commandeFournisseurApiService.listerCommandes();
      const mapped = records.map((rec) => this.mapApiRecord(rec));
      this.mettreAJourCache(mapped);
      return mapped;
    } catch (error) {
      console.error('Erreur API commandes fournisseur, fallback localStorage:', error);
      const fallback = this.lireLocalHistorique();
      this.cache = fallback;
      return fallback;
    }
  }

  private mettreAJourInfosDevisLocal(
    devisIds: string[],
    numeroCommande: string,
    statut: CommandeHistorique['statut'],
  ): void {
    const devisInfos = this.getDevisCommandesInfoLocal();
    const dateCommande = new Date().toISOString();

    devisIds.forEach((devisId) => {
      if (!devisInfos[devisId]) {
        devisInfos[devisId] = {
          devisId,
          commandesAssociees: [],
        };
      }

      devisInfos[devisId].commandesAssociees.push({
        numeroCommande,
        dateCommande,
        statut,
      });

      devisInfos[devisId].derniereDateCommande = dateCommande;
    });

    this.sauvegarderDevisCommandesInfo(devisInfos);
  }

  async ajouterCommandeAHistorique(commande: CommandeGroupee, devisIds: string[]): Promise<CommandeHistorique | null> {
    try {
      const created = await commandeFournisseurApiService.creerCommande(commande, 'BROUILLON');
      const mapped = this.mapApiRecord(created);
      const historique = [mapped, ...(this.cache || [])];
      // Garder seulement les 100 dernières commandes
      this.mettreAJourCache(historique.slice(0, 100));
      this.mettreAJourInfosDevisLocal(devisIds, mapped.numeroCommande, mapped.statut);
      return mapped;
    } catch (error) {
      console.error("Erreur lors de l'ajout à l'historique via API, fallback local:", error);
      const historique = this.cache.length ? [...this.cache] : this.lireLocalHistorique();
      const nouvelleEntree: CommandeHistorique = {
        id: this.genererIdHistorique(),
        numeroCommande: commande.numeroCommande,
        dateCreation: commande.dateCreation,
        devisInclus: commande.devisInclus,
        totaux: commande.totaux,
        statut: 'BROUILLON',
        notes: `Commande générée automatiquement - ${commande.matieres.length} matières, ${commande.fournitures.length} fournitures`,
      };
      historique.unshift(nouvelleEntree);
      this.mettreAJourCache(historique.slice(0, 100));
      this.mettreAJourInfosDevisLocal(devisIds, commande.numeroCommande, 'BROUILLON');
      return nouvelleEntree;
    }
  }

  async mettreAJourStatutCommande(
    commandeId: string,
    nouveauStatut: CommandeHistorique['statut'],
    notes?: string,
  ): Promise<void> {
    try {
      const updated = await commandeFournisseurApiService.majStatut(commandeId, nouveauStatut);
      const mapped = this.mapApiRecord(updated);
      const historique = await this.getHistorique();
      const updatedHistorique = historique.map((c) =>
        c.id === mapped.id || c.numeroCommande === mapped.numeroCommande
          ? { ...mapped, notes: notes ?? mapped.notes ?? c.notes }
          : c,
      );
      this.mettreAJourCache(updatedHistorique);
      this.mettreAJourInfosDevisLocal(mapped.devisInclus, mapped.numeroCommande, mapped.statut);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut via API:', error);
    }
  }

  async devisDejaCommande(devisId: string): Promise<boolean> {
    const historique = await this.getHistorique();
    return historique.some((commande) => commande.devisInclus.includes(devisId));
  }

  async getStatistiques(): Promise<{
    totalCommandes: number;
    commandesEnCours: number;
    commandesRecues: number;
    montantTotalCommande: number;
    derniereMiseAJour: string | null;
  }> {
    const historique =
      this.cache.length > 0 ? this.cache : await this.getHistorique();
    return {
      totalCommandes: historique.length,
      commandesEnCours: historique.filter((c) => c.statut === 'BROUILLON' || c.statut === 'ENVOYEE').length,
      commandesRecues: historique.filter((c) => c.statut === 'RECUE').length,
      montantTotalCommande: historique.reduce((total, c) => total + (c.totaux?.montantTotal || 0), 0),
      derniereMiseAJour: historique.length > 0 ? historique[0].dateCreation : null,
    };
  }

  async rechercherDansHistorique(terme: string): Promise<CommandeHistorique[]> {
    const historique = await this.getHistorique();
    const termeLower = terme.toLowerCase();

    return historique.filter(
      (commande) =>
        commande.numeroCommande.toLowerCase().includes(termeLower) ||
        commande.devisInclus.some((ref) => ref.toLowerCase().includes(termeLower)) ||
        (commande.notes && commande.notes.toLowerCase().includes(termeLower)),
    );
  }

  async exporterHistorique(): Promise<void> {
    try {
      const historique = await this.getHistorique();

      let csvContent = '\uFEFF'; // BOM UTF-8
      csvContent += 'Numéro commande;Date création;Statut;Nombre devis;Montant total;Devis inclus;Notes\n';

      historique.forEach((commande) => {
        const dateCreation = new Date(commande.dateCreation).toLocaleDateString('fr-FR');
        const devisInclus = commande.devisInclus.join(', ');
        const notes = (commande.notes || '').replace(/;/g, ','); // Remplacer ; par ,

        csvContent += `"${commande.numeroCommande}";"${dateCreation}";"${commande.statut}";${commande.totaux.nombreDevis};${commande.totaux.montantTotal.toFixed(2)};"${devisInclus}";"${notes}"\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `historique_commandes_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erreur lors de l'export de l'historique:", error);
    }
  }

  async nettoyerHistorique(joursAConserver: number = 365): Promise<void> {
    try {
      const historique = await this.getHistorique();
      const dateLimit = new Date();
      dateLimit.setDate(dateLimit.getDate() - joursAConserver);

      const historiqueFiltre = historique.filter((commande) => new Date(commande.dateCreation) > dateLimit);
      this.mettreAJourCache(historiqueFiltre);
    } catch (error) {
      console.error("Erreur lors du nettoyage de l'historique:", error);
    }
  }
}

export const commandeHistoriqueService = new CommandeHistoriqueService();
